/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Talking Head Avatar & AI Lip-Sync State Management Store (Task 10.4.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';
import { useWalletStore } from './useWalletStore';

export type AvatarBackgroundPreset = 'transparent' | 'green_screen' | 'office' | 'studio' | 'custom';
export type AvatarAspectRatio = '16:9' | '9:16' | '1:1';
export type AvatarModelId = 'synthesia' | 'kling' | 'modelslab';

export interface AvatarPresenter {
  id: string;
  name: string;
  avatarUrl: string;
  roleKey: string;
  nationality: string;
  gender: 'female' | 'male';
  isCustom?: boolean;
}

export interface AvatarVoiceOption {
  id: string;
  name: string;
  provider: 'openai' | 'elevenlabs' | 'azure' | 'google';
  language: string;
  gender: 'female' | 'male';
  sampleAudioUrl: string;
}

export interface GeneratedAvatarClip {
  id: string;
  videoUrl: string;
  thumbnailUrl: string;
  title?: string;
  script: string;
  avatarId: string;
  presenterName: string;
  voiceId: string;
  voiceName: string;
  background: AvatarBackgroundPreset;
  customBackgroundUrl?: string;
  model: string;
  aspectRatio: AvatarAspectRatio;
  durationSeconds: number;
  createdAt: string;
}

export interface SampleAvatarScript {
  id: string;
  title: string;
  script: string;
}

export interface TalkingAvatarState {
  // Config parameters
  selectedAvatarId: string;
  customAvatarUrl: string | null;
  script: string;
  selectedVoiceId: string;
  background: AvatarBackgroundPreset;
  customBackgroundUrl: string;
  model: AvatarModelId;
  aspectRatio: AvatarAspectRatio;
  title: string;

  // Audio preview state
  isPlayingVoiceSample: boolean;

  // Generation status
  isGenerating: boolean;
  generationProgress: number; // 0 - 100
  generationStage: string;
  errorMessage: string | null;

  // Video feed & active selection
  activeVideo: GeneratedAvatarClip | null;
  avatarVideos: GeneratedAvatarClip[];

  // Actions
  setSelectedAvatarId: (id: string) => void;
  setCustomAvatarUrl: (url: string | null) => void;
  setScript: (script: string) => void;
  setSelectedVoiceId: (voiceId: string) => void;
  setBackground: (bg: AvatarBackgroundPreset) => void;
  setCustomBackgroundUrl: (url: string) => void;
  setModel: (model: AvatarModelId) => void;
  setAspectRatio: (aspectRatio: AvatarAspectRatio) => void;
  setTitle: (title: string) => void;
  setActiveVideo: (video: GeneratedAvatarClip | null) => void;
  applySampleScript: (script: string) => void;

  playVoicePreview: () => Promise<void>;
  generateAvatarVideo: () => Promise<boolean>;
  deleteAvatarVideo: (id: string) => void;
  resetStudio: () => void;
}

export const AVATAR_BASE_COST = 30; // 30 credits standard base cost

export const AVATAR_PRESENTERS: AvatarPresenter[] = [
  {
    id: 'anna_news',
    name: 'Anna Vance',
    avatarUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&auto=format&fit=crop&q=80',
    roleKey: 'avatar.roleNewsAnchor',
    nationality: 'American',
    gender: 'female',
  },
  {
    id: 'linh_presenter',
    name: 'Mai Linh',
    avatarUrl: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&auto=format&fit=crop&q=80',
    roleKey: 'avatar.roleTrainer',
    nationality: 'Vietnamese',
    gender: 'female',
  },
  {
    id: 'david_exec',
    name: 'David Chen',
    avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&auto=format&fit=crop&q=80',
    roleKey: 'avatar.roleTechPresenter',
    nationality: 'Canadian',
    gender: 'male',
  },
  {
    id: 'sarah_consultant',
    name: 'Sarah Jenkins',
    avatarUrl: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&auto=format&fit=crop&q=80',
    roleKey: 'avatar.roleCustomerCare',
    nationality: 'British',
    gender: 'female',
  },
  {
    id: 'alex_educator',
    name: 'Alexandre Dubois',
    avatarUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&auto=format&fit=crop&q=80',
    roleKey: 'avatar.roleEducator',
    nationality: 'French',
    gender: 'male',
  },
];

export const AVATAR_VOICES: AvatarVoiceOption[] = [
  {
    id: 'vi_vn_hoaimy',
    name: 'Hoài My (Azure Neural)',
    provider: 'azure',
    language: 'vi-VN',
    gender: 'female',
    sampleAudioUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/sample-audio.mp3',
  },
  {
    id: 'vi_vn_namminh',
    name: 'Nam Minh (Azure Neural)',
    provider: 'azure',
    language: 'vi-VN',
    gender: 'male',
    sampleAudioUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/sample-audio.mp3',
  },
  {
    id: 'openai_nova',
    name: 'Nova (OpenAI Natural)',
    provider: 'openai',
    language: 'en-US',
    gender: 'female',
    sampleAudioUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/sample-audio.mp3',
  },
  {
    id: 'openai_alloy',
    name: 'Alloy (OpenAI Natural)',
    provider: 'openai',
    language: 'en-US',
    gender: 'male',
    sampleAudioUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/sample-audio.mp3',
  },
  {
    id: 'eleven_rachel',
    name: 'Rachel (ElevenLabs Studio)',
    provider: 'elevenlabs',
    language: 'en-GB',
    gender: 'female',
    sampleAudioUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/sample-audio.mp3',
  },
];

export const SAMPLE_SCRIPTS: SampleAvatarScript[] = [
  {
    id: 'script-welcome',
    title: 'Corporate Onboarding Welcome',
    script:
      'Welcome to Sophia Platform, the enterprise edge-native AI operating system. Today, we will explore our intelligent workflow automation tools designed to empower your business.',
  },
  {
    id: 'script-product-demo',
    title: 'Product Launch & Overview',
    script:
      'We are excited to unveil our new AI Media Studio. Experience ultra-realistic cinematic video synthesis, high-resolution 4K upscaling, and natural talking avatar presentations with zero latency.',
  },
  {
    id: 'script-training',
    title: 'Educational Course Introduction',
    script:
      'Hello learners! In this training module, we will break down the essential concepts of modern cloud computing and artificial intelligence. Let us get started with chapter one.',
  },
];

const INITIAL_AVATAR_VIDEOS: GeneratedAvatarClip[] = [
  {
    id: 'avatar-vid-01',
    videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&auto=format&fit=crop&q=80',
    title: 'Welcome to Sophia Platform',
    script:
      'Welcome to Sophia Platform, the enterprise edge-native AI operating system built entirely on Cloudflare Workers.',
    avatarId: 'anna_news',
    presenterName: 'Anna Vance',
    voiceId: 'openai_nova',
    voiceName: 'Nova (OpenAI Natural)',
    background: 'office',
    model: 'synthesia',
    aspectRatio: '16:9',
    durationSeconds: 15,
    createdAt: '2026-10-25T06:40:00Z',
  },
  {
    id: 'avatar-vid-02',
    videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&auto=format&fit=crop&q=80',
    title: 'Giới Thiệu Hệ Thống AI Đa Phương Thức',
    script:
      'Chào mừng bạn đến với xưởng sáng tạo MC ảo của Sophia Platform. Bạn có thể dễ dàng chuyển đổi văn bản thành video thuyết trình chuyên nghiệp.',
    avatarId: 'linh_presenter',
    presenterName: 'Mai Linh',
    voiceId: 'vi_vn_hoaimy',
    voiceName: 'Hoài My (Azure Neural)',
    background: 'studio',
    model: 'synthesia',
    aspectRatio: '16:9',
    durationSeconds: 18,
    createdAt: '2026-10-25T06:20:00Z',
  },
];

export const useTalkingAvatarStore = create<TalkingAvatarState>((set, get) => ({
  selectedAvatarId: 'anna_news',
  customAvatarUrl: null,
  script:
    'Welcome to Sophia Platform, the enterprise edge-native AI operating system. Today, we will explore our intelligent workflow automation tools designed to empower your business.',
  selectedVoiceId: 'openai_nova',
  background: 'office',
  customBackgroundUrl: '',
  model: 'synthesia',
  aspectRatio: '16:9',
  title: 'Sophia Platform Introduction',

  isPlayingVoiceSample: false,
  isGenerating: false,
  generationProgress: 0,
  generationStage: '',
  errorMessage: null,

  activeVideo: INITIAL_AVATAR_VIDEOS[0],
  avatarVideos: INITIAL_AVATAR_VIDEOS,

  setSelectedAvatarId: (id) => set({ selectedAvatarId: id }),
  setCustomAvatarUrl: (url) => set({ customAvatarUrl: url }),
  setScript: (script) => set({ script: script.slice(0, 5000) }),
  setSelectedVoiceId: (voiceId) => set({ selectedVoiceId: voiceId }),
  setBackground: (background) => set({ background }),
  setCustomBackgroundUrl: (url) => set({ customBackgroundUrl: url }),
  setModel: (model) => set({ model }),
  setAspectRatio: (aspectRatio) => set({ aspectRatio }),
  setTitle: (title) => set({ title }),
  setActiveVideo: (video) => set({ activeVideo: video }),
  applySampleScript: (script) => set({ script }),

  playVoicePreview: async () => {
    const { isPlayingVoiceSample } = get();
    if (isPlayingVoiceSample) return;

    set({ isPlayingVoiceSample: true });
    try {
      // Simulate speech audition audio preview playback (1.5 seconds)
      await new Promise((resolve) => setTimeout(resolve, 1500));
      set({ isPlayingVoiceSample: false });
    } catch {
      set({ isPlayingVoiceSample: false });
    }
  },

  generateAvatarVideo: async () => {
    const {
      selectedAvatarId,
      customAvatarUrl,
      script,
      selectedVoiceId,
      background,
      customBackgroundUrl,
      model,
      aspectRatio,
      title,
    } = get();

    if (!script.trim()) {
      set({ errorMessage: 'Please provide speech script text.' });
      return false;
    }

    const walletStore = useWalletStore.getState();
    if (walletStore.balance < AVATAR_BASE_COST) {
      set({
        errorMessage: 'Insufficient wallet credits. Please top up your balance.',
      });
      return false;
    }

    // Deduct credits optimistically
    walletStore.optimisticDeduct(AVATAR_BASE_COST);

    set({
      isGenerating: true,
      generationProgress: 10,
      generationStage: 'Analyzing script phonemes and speech cadence...',
      errorMessage: null,
    });

    try {
      // Find presenter info
      const presenter =
        AVATAR_PRESENTERS.find((p) => p.id === selectedAvatarId) || {
          id: 'custom',
          name: 'Custom Portrait',
          avatarUrl: customAvatarUrl || '',
          roleKey: 'avatar.customAvatarTab',
          nationality: 'Global',
          gender: 'female' as const,
        };

      // Find voice info
      const voice =
        AVATAR_VOICES.find((v) => v.id === selectedVoiceId) || AVATAR_VOICES[0];

      // Call API endpoint if available
      let remoteVideoUrl: string | null = null;
      try {
        const payload = {
          script,
          avatarId: selectedAvatarId,
          voiceId: selectedVoiceId,
          background,
          customBackgroundUrl: customBackgroundUrl || undefined,
          model,
          aspectRatio,
          title: title || undefined,
        };

        const response = await fetch('/api/v1/media/videos/talking-avatar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (response.ok || response.status === 202) {
          const json = (await response.json()) as any;
          if (json?.data?.videoUrl) {
            remoteVideoUrl = json.data.videoUrl;
          }
        }
      } catch {
        // Fall back to client simulation
      }

      // Step-by-step progress stages
      const stages = [
        { progress: 30, stage: 'Generating neural TTS audio stream...' },
        { progress: 55, stage: 'Calculating 3D facial landmark mesh & phoneme sync...' },
        { progress: 80, stage: 'Compositing avatar with studio setting & lighting...' },
        { progress: 95, stage: 'Encoding MP4 video stream with H.264 profile...' },
      ];

      for (const st of stages) {
        await new Promise((resolve) => setTimeout(resolve, 400));
        set({ generationProgress: st.progress, generationStage: st.stage });
      }

      await new Promise((resolve) => setTimeout(resolve, 300));

      const newAvatarVideo: GeneratedAvatarClip = {
        id: `avatar-vid-${Date.now()}`,
        videoUrl:
          remoteVideoUrl ||
          'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
        thumbnailUrl: customAvatarUrl || presenter.avatarUrl,
        title: title || presenter.name,
        script,
        avatarId: selectedAvatarId,
        presenterName: presenter.name,
        voiceId: selectedVoiceId,
        voiceName: voice.name,
        background,
        customBackgroundUrl: customBackgroundUrl || undefined,
        model,
        aspectRatio,
        durationSeconds: Math.max(5, Math.min(60, Math.ceil(script.split(' ').length / 2.5))),
        createdAt: new Date().toISOString(),
      };

      set((state) => ({
        isGenerating: false,
        generationProgress: 100,
        generationStage: 'Talking avatar video rendered successfully!',
        avatarVideos: [newAvatarVideo, ...state.avatarVideos],
        activeVideo: newAvatarVideo,
      }));

      return true;
    } catch (err: any) {
      // Rollback deduction on error
      walletStore.rollbackDeduct(AVATAR_BASE_COST);
      set({
        isGenerating: false,
        generationProgress: 0,
        errorMessage: err?.message || 'Avatar video rendering failed. Credits refunded.',
      });
      return false;
    }
  },

  deleteAvatarVideo: (id: string) => {
    set((state) => {
      const filtered = state.avatarVideos.filter((v) => v.id !== id);
      return {
        avatarVideos: filtered,
        activeVideo: state.activeVideo?.id === id ? filtered[0] || null : state.activeVideo,
      };
    });
  },

  resetStudio: () => {
    set({
      selectedAvatarId: 'anna_news',
      customAvatarUrl: null,
      script:
        'Welcome to Sophia Platform, the enterprise edge-native AI operating system. Today, we will explore our intelligent workflow automation tools designed to empower your business.',
      selectedVoiceId: 'openai_nova',
      background: 'office',
      customBackgroundUrl: '',
      model: 'synthesia',
      aspectRatio: '16:9',
      title: 'Sophia Platform Introduction',
      isGenerating: false,
      isPlayingVoiceSample: false,
      generationProgress: 0,
      errorMessage: null,
    });
  },
}));
